import React, { useEffect } from 'react';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';

import LoaderOverlay from 'components/PageLayout/LoaderOverlay';
import { CuroAppRoute } from 'enums/curo/CuroAppRoute';
import { getApplicationSelector, getSignInProcessState } from 'handlers/curo-handlers/selectors';
import { useAppSelector } from 'hooks/reduxHooks';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { useLoadingState } from 'hooks/useLoadingState';
import { getCurrentAccount, getCurrentApplication } from 'thunks';
import TokenUtils from 'utils/TokenUtils';

const RequiredAuth = () => {
  const [isLoading, setIsLoading] = useLoadingState();
  const { accountData } = useAppSelector(getSignInProcessState);
  const application = useAppSelector(getApplicationSelector);
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const navigate = useNavigate();

  const checkSession = async () => {
    const accessToken = TokenUtils.getAccessToken();
    const refreshToken = TokenUtils.getRefreshToken();

    if (!accessToken && !refreshToken) {
      setIsLoading('error');
      return;
    }

    setIsLoading('loading');
    try {
      if (!accountData) {
        await dispatchWithUnwrap(getCurrentAccount());
      }

      if (!application) {
        await dispatchWithUnwrap(getCurrentApplication());
      }
      setIsLoading('success');
    } catch (error) {
      setIsLoading('error');
    }
  };

  useEffect(() => {
    checkSession();
  }, []);

  useEffect(() => {
    if (!accountData && !application && isLoading !== 'idle') {
      navigate(CuroAppRoute.SignIn);
    }
  }, [accountData]);

  if (!accountData || !application) {
    if (isLoading === 'idle' || isLoading === 'loading') {
      return <LoaderOverlay />;
    }

    if (accountData && !application) {
      return <Navigate to={CuroAppRoute.Home} />;
    }

    return <Navigate to={CuroAppRoute.SignIn} />;
  }

  return <Outlet />;
};

export default RequiredAuth;
